// @ts-nocheck
import React, { useMemo, useCallback } from 'react';
import { FixedSizeList as List, FixedSizeGrid as Grid } from 'react-window';
import {
  Book, Eye, Download, FileText, Calendar, User, Tag,
  Clock, CheckCircle, AlertCircle, Loader, MoreVertical,
  ExternalLink, X, History, TrendingUp, Star, CalendarDays,
  BookOpen, Edit
} from 'lucide-react';
import BookCover from './BookCover';
import './VirtualizedBookList.css';

/**
 * Nombre de colonnes tenant dans la largeur, avec AU MOINS une colonne.
 * Sous la largeur d'une vignette (conteneur étroit, ou pas encore mesuré),
 * `Math.floor` renvoyait 0 : la grille affichait alors zéro livre et le nombre
 * de lignes valait `Math.ceil(n / 0)` = Infinity (audit BIB-008).
 */
const colonnesTenables = (containerWidth, itemWidth) =>
  Math.max(1, Math.floor(containerWidth / itemWidth));

const VirtualizedBookList = ({
  books,
  viewMode,
  onBookAction,
  containerHeight = 600,
  containerWidth = 1200
}) => {

  // Configuration des tailles selon le mode d'affichage
  const config = useMemo(() => {
    switch (viewMode) {
      case 'grid':
        return {
          itemWidth: 200,
          itemHeight: 280,
          columns: colonnesTenables(containerWidth, 200),
          rows: Math.ceil(books.length / colonnesTenables(containerWidth, 200))
        };
      case 'list':
        return {
          itemWidth: containerWidth,
          itemHeight: 80,
          columns: 1,
          rows: books.length
        };
      case 'cards':
        return {
          itemWidth: 300,
          itemHeight: 200,
          columns: colonnesTenables(containerWidth, 300),
          rows: Math.ceil(books.length / colonnesTenables(containerWidth, 300))
        };
      case 'timeline':
        return {
          itemWidth: containerWidth,
          itemHeight: 120,
          columns: 1,
          rows: books.length
        };
      default:
        return {
          itemWidth: 200,
          itemHeight: 280,
          columns: colonnesTenables(containerWidth, 200),
          rows: Math.ceil(books.length / colonnesTenables(containerWidth, 200))
        };
    }
  }, [viewMode, books.length, containerWidth, containerHeight]);

  // Composant pour l'affichage en grille
  const GridItem = useCallback(({ columnIndex, rowIndex, style }) => {
    const index = rowIndex * config.columns + columnIndex;
    const book = books[index];
    
    if (!book) return null;

    return (
      <div style={style} className="virtualized-grid-item">
        <BookCard book={book} onBookAction={onBookAction} />
      </div>
    );
  }, [books, config.columns, onBookAction]);

  // Composant pour l'affichage en liste
  const ListItem = useCallback(({ index, style }) => {
    const book = books[index];
    
    if (!book) return null;

    return (
      <div style={style} className="virtualized-list-item">
        {viewMode === 'list' && <BookListItem book={book} onBookAction={onBookAction} />}
        {viewMode === 'timeline' && <BookTimelineItem book={book} onBookAction={onBookAction} />}
      </div>
    );
  }, [books, viewMode, onBookAction]);

  // Rendu selon le mode d'affichage
  if (viewMode === 'grid' || viewMode === 'cards') {
    return (
      <div className="virtualized-grid-container">
        <Grid
          columnCount={config.columns}
          columnWidth={config.itemWidth}
          height={containerHeight}
          rowCount={config.rows}
          rowHeight={config.itemHeight}
          width={containerWidth}
          itemData={books}
        >
          {GridItem}
        </Grid>
      </div>
    );
  }

  return (
    <div className="virtualized-list-container">
      <List
        height={containerHeight}
        itemCount={books.length}
        itemSize={config.itemHeight}
        width={containerWidth}
      >
        {ListItem}
      </List>
    </div>
  );
};

// Composant BookCard pour l'affichage en grille
const BookCard = ({ book, onBookAction }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'indexed':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'received':
      case 'processing':
        return <Loader size={16} className="text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="book-card">
      <BookCover
        book={book}
        size="medium"
        showStatus={true}
        className="book-card-cover"
      />

      <div className="book-info">
        <div className="book-title">
          {book.title || book.file_original_name}
        </div>
        
        {book.author && (
          <div className="book-author">
            <User size={16} />
            <span>{book.author}</span>
          </div>
        )}

        <div className="book-meta">
          <div className="book-meta-item">
            <Calendar size={16} />
            <span>{formatDate(book.created_at)}</span>
          </div>
          
          {book.tags_json && (() => { 
            try { 
              const tags = JSON.parse(book.tags_json); 
              return tags.length > 0; 
            } catch { 
              return false; 
            } 
          })() && (
            <div className="book-tags">
              {(() => { 
                try { 
                  return JSON.parse(book.tags_json).slice(0, 2); 
                } catch { 
                  return []; 
                } 
              })().map((tag, index) => (
                <span key={index} className="book-tag">
                  <Tag size={16} />
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="book-actions">
          <button
            className="book-action primary"
            onClick={() => onBookAction(book.book_id, 'read')}
            title="Lire"
          >
            <Eye size={16} />
            Lire
          </button>
          
          <button
            className="book-action"
            onClick={() => onBookAction(book.book_id, 'ask')}
            title="Interroger avec l'IA"
          >
            <FileText size={16} />
            IA
          </button>

          {/* L'édition des métadonnées n'était atteignable depuis aucune vue
              rendue : les cartes porteuses du bouton (dans Bibliotheque.tsx)
              ne sont plus utilisées depuis le passage à la virtualisation. */}
          <button
            className="book-action"
            onClick={() => onBookAction(book.book_id, 'edit')}
            title="Modifier les métadonnées"
            aria-label="Modifier les métadonnées"
          >
            <Edit size={16} />
            Modifier
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant BookListItem pour l'affichage en liste
const BookListItem = ({ book, onBookAction }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'indexed':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'received':
      case 'processing':
        return <Loader size={16} className="text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'indexed': 'Indexé',
      'received': 'Reçu',
      'processing': 'En cours',
      'error': 'Erreur'
    };
    return labels[status] || status;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="book-list-item">
      <div className="book-list-cover">
        <Book size={24} />
      </div>

      <div className="book-list-info">
        <div className="book-list-main">
          <div className="book-list-title">
            {book.title || book.file_original_name}
          </div>
          {book.author && (
            <div className="book-list-author">{book.author}</div>
          )}
        </div>

        <div className="book-list-meta">
          <div className="book-list-status">
            {getStatusIcon(book.status)}
            <span>{getStatusLabel(book.status)}</span>
          </div>
          
          <div className="book-list-date">
            {formatDate(book.created_at)}
          </div>
        </div>

        <div className="book-list-tags">
          {book.tags_json && (() => { 
            try { 
              return JSON.parse(book.tags_json); 
            } catch { 
              return []; 
            } 
          })().map((tag, index) => (
            <span key={index} className="book-list-tag">{tag}</span>
          ))}
        </div>
      </div>

      <div className="book-list-actions">
        <button
          className="book-action primary"
          onClick={() => onBookAction(book.book_id, 'read')}
        >
          <Eye size={16} />
        </button>
        
        <button
          className="book-action"
          onClick={() => onBookAction(book.book_id, 'reader')}
          title="Lecteur Avancé"
        >
          <BookOpen size={16} />
        </button>
        
        <button
          className="book-action"
          onClick={() => onBookAction(book.book_id, 'ask')}
        >
          <FileText size={16} />
        </button>

        <button
          className="book-action"
          onClick={() => onBookAction(book.book_id, 'edit')}
          title="Modifier les métadonnées"
          aria-label="Modifier les métadonnées"
        >
          <Edit size={16} />
        </button>
      </div>
    </div>
  );
};

// Composant BookTimelineItem pour l'affichage en timeline
const BookTimelineItem = ({ book, onBookAction }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'indexed':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'received':
      case 'processing':
        return <Loader size={16} className="text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  return (
    <div className="book-timeline-item">
      <div className="book-timeline-date">
        <div className="book-timeline-day">
          {new Date(book.created_at * 1000).getDate()}
        </div>
        <div className="book-timeline-month">
          {new Date(book.created_at * 1000).toLocaleDateString('fr-FR', { month: 'short' })}
        </div>
      </div>

      <div className="book-timeline-content">
        <div className="book-timeline-header">
          <div className="book-timeline-title">
            {book.title || book.file_original_name}
          </div>
          <div className="book-timeline-status">
            {getStatusIcon(book.status)}
          </div>
        </div>

        {book.author && (
          <div className="book-timeline-author">
            <User size={16} />
            <span>{book.author}</span>
          </div>
        )}

        <div className="book-timeline-meta">
          {book.series && (
            <span className="book-timeline-series">{book.series}</span>
          )}
          {book.year && (
            <span className="book-timeline-year">{book.year}</span>
          )}
          {book.lang && (
            <span className="book-timeline-lang">{book.lang}</span>
          )}
        </div>

        <div className="book-timeline-actions">
          <button
            className="book-action primary"
            onClick={() => onBookAction(book.book_id, 'read')}
            aria-label="Lire le livre"
          >
            <Eye size={16} />
            Lire
          </button>
          
          <button
            className="book-action"
            onClick={() => onBookAction(book.book_id, 'koreader')}
            aria-label="Ouvrir avec KOReader"
          >
            <Book size={16} />
            KOReader
          </button>
          
          <button
            className="book-action"
            onClick={() => onBookAction(book.book_id, 'ask')}
            aria-label="Interroger avec l'IA"
          >
            <FileText size={16} />
            IA
          </button>

          <button
            className="book-action"
            onClick={() => onBookAction(book.book_id, 'edit')}
            aria-label="Modifier les métadonnées"
          >
            <Edit size={16} />
            Modifier
          </button>
        </div>
      </div>
    </div>
  );
};

export default VirtualizedBookList;
